import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Home, User, Briefcase, BookOpen, Star, Send } from 'lucide-react'
import { trackSectionView } from '../utils/analytics'

const navItems = [
  { id: 'hero', label: 'Home', icon: Home },
  { id: 'story', label: 'The Story', icon: User },
  { id: 'projects', label: 'The Projects', icon: Briefcase },
  { id: 'insights', label: 'The Insights', icon: BookOpen },
  { id: 'offer', label: 'The Offer', icon: Star },
  { id: 'contact', label: 'Contact', icon: Send },
]

const FloatingNavigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('hero')

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => document.getElementById(item.id))
      const scrollPosition = window.scrollY + 100

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navItems[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId)
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' })
      // Track section navigation
      trackSectionView(sectionId)
    }
    setIsOpen(false)
  }

  return (
    <nav 
      className="fixed top-8 right-8 z-50"
      aria-label="Main navigation"
      role="navigation"
    >
      {/* Toggle button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 bg-gray-900/80 backdrop-blur-xs border border-electric-cyan/50 rounded-full flex items-center justify-center text-electric-cyan hover:bg-electric-cyan hover:text-space-black transition-all duration-300"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
        aria-expanded={isOpen}
        aria-controls="floating-nav-menu"
      >
        {isOpen ? <X size={20} /> : <Menu size={20} />}
      </motion.button>

      {/* Navigation menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="floating-nav-menu"
            initial={{ opacity: 0, scale: 0.8, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{ duration: 0.3 }}
            className="absolute top-16 right-0 bg-gray-900/90 backdrop-blur-xs border border-gray-700/50 rounded-lg p-4 min-w-[200px]"
            role="menu"
            aria-label="Navigation menu"
          >
            <nav className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon
                const isActive = activeSection === item.id
                
                return (
                  <motion.button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-300 ${
                      isActive
                        ? 'bg-electric-cyan/20 text-electric-cyan border border-electric-cyan/30'
                        : 'text-light-gray hover:bg-gray-800/50 hover:text-electric-cyan'
                    }`}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    role="menuitem"
                    aria-label={`Navigate to ${item.label} section`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon size={18} />
                    <span className="font-inter text-sm">{item.label}</span>
                  </motion.button>
                )
              })}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default FloatingNavigation 